import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FaqBackToTop } from '../components/faq/FaqBackToTop';
import { FaqCategoryIndex } from '../components/faq/FaqCategoryIndex';
import { FaqExplorer } from '../components/faq/FaqExplorer';
import { FaqPageFooter } from '../components/faq/FaqPageFooter';
import { FaqPopularTopics } from '../components/faq/FaqPopularTopics';
import { FaqShortcutCheatSheet } from '../components/faq/FaqShortcutCheatSheet';
import { FaqWorkflowGuide } from '../components/faq/FaqWorkflowGuide';
import './FaqPage.css';

export function FaqPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const qParam = params.get('q') ?? '';
  const idParam = params.get('id') ?? '';
  const catParam = params.get('cat') ?? '';

  const printManual = useCallback(() => {
    window.print();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [qParam, idParam, catParam]);

  useEffect(() => {
    const prev = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute('content') ?? '';
    document.title = `${t('faq.pageTitle')} — ${t('page.title')}`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', t('faq.metaDescription'));
    return () => {
      document.title = prev;
      if (prevDesc) meta?.setAttribute('content', prevDesc);
    };
  }, [t]);

  return (
    <div className="saas-faq-page">
      <header className="saas-faq-page__hero">
        <div className="saas-faq-page__hero-orbs" aria-hidden="true">
          <div className="saas-faq-page__orb saas-faq-page__orb--1" />
          <div className="saas-faq-page__orb saas-faq-page__orb--2" />
          <div className="saas-faq-page__orb saas-faq-page__orb--3" />
        </div>
        <span className="saas-faq-page__hero-badge">
          <span aria-hidden="true">☁️</span> {t('faq.pageBadge')}
        </span>
        <h1 className="saas-faq-page__hero-title">{t('faq.pageTitle')}</h1>
        <p className="saas-faq-page__hero-sub">{t('faq.pageSub')}</p>
        <p className="saas-faq-page__hero-note">{t('faq.pageNote')}</p>
        <div className="saas-faq-page__hero-actions">
          <button type="button" className="saas-faq-page__print" onClick={printManual}>
            {t('faq.printManual')}
          </button>
          <a href="#saas-faq-explorer" className="saas-faq-page__skip">
            {t('faq.skipToSearch')}
          </a>
        </div>
      </header>

      <div className="saas-faq-page__body">
        <section className="saas-faq-page__section saas-faq-page__section--card">
          <FaqCategoryIndex />
        </section>

        <section className="saas-faq-page__section saas-faq-page__section--card">
          <FaqWorkflowGuide />
        </section>

        <div className="saas-faq-page__duo">
          <section className="saas-faq-page__section saas-faq-page__section--card">
            <FaqShortcutCheatSheet />
          </section>
          <section className="saas-faq-page__section saas-faq-page__section--card">
            <FaqPopularTopics />
          </section>
        </div>

        <section className="saas-faq-page__section saas-faq-page__section--explorer">
          <FaqExplorer
            variant="page"
            initialQuery={qParam}
            initialItemId={idParam}
            initialCategoryId={catParam}
          />
        </section>
      </div>

      <FaqPageFooter />
      <FaqBackToTop />
    </div>
  );
}
